import React, { useEffect, useRef } from "react";
import { string, arrayOf, number, func, bool } from "prop-types";
import { Field, Form, Formik } from "formik";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import moment from "moment";
import { Col, Row } from "react-bootstrap";
import { actions as echeckActions } from "reducers/echecks";
import Button from "pages/_components/Button";
import Selector from "pages/_components/fields/formik/Selector";
import RepudiateEcheckForm from "pages/echeck/EcheckActions/components/RepudiateForm";
import DepositEcheckForm from "pages/echeck/EcheckActions/components/DepositForm";
import CustodyEcheq from "pages/echeck/EcheckActions/components/CustodyForm";
import { multipleEcheckActions } from "util/echecks";
import * as i18n from "util/i18n";
import useSchema from "hooks/useSchema";
import * as Yup from "yup";
import { echeqPropTypes } from "pages/echeck/echeckPropTypes";
import "./MultipleActionSelector.scss";
import { resizableRoute } from "pages/_components/Resizable";
import * as config from "util/config";
import AnulateEcheckForm from "pages/echeck/EcheckActions/components/AnulateForm";
import AnulateCesionEcheq from "pages/echeck/EmitCesion/components/AnulateForm";

const FORM_ID = "echeq";

const MultipleActionSelector = ({ selectedRows, environmentCuit, echeqsListTotal, setDrawer, isDesktop }) => {
    const dispatch = useDispatch();
    const { stringRequired } = useSchema();
    const formRef = useRef(null);
    const validationSchema = Yup.object().shape({ action: stringRequired });
    const someIsExpired = selectedRows.some((echeq) => {
        const defaultTZ = config.get("administration.timezone.defaultValue", "America/Argentina/Buenos_Aires");
        const fechaPago = moment(echeq.fecha_pago).tz(defaultTZ);
        const fechaHoy = moment().tz(defaultTZ);
        const diff = Math.abs(fechaPago.diff(fechaHoy, "days"));

        if (diff < 0) {
            return echeq;
        }
    });

    useEffect(() => {
        if (!formRef.current) {
            return;
        }
        formRef.current.setFieldValue("action", "");
    }, [selectedRows]);

    const checkEnableAdmit = (button) => {
        let disable = !selectedRows.length > 0;
        if (button === "ADMIT" || button === "REPUDIATE") {
            selectedRows.forEach((echeq) => {
                if (
                    (echeq.estado === "ACTIVO-PENDIENTE" &&
                        (echeq.total_endosos === 0 || echeq.tenencia_beneficiario_documento !== environmentCuit)) ||
                    echeq.estado === "ACTIVO"
                ) {
                    disable = true;
                }
            });
        }
        switch (button) {
            case "CUSTODY": {
                selectedRows.forEach((echeq) => {
                    if (echeq.estado !== "ACTIVO" || moment(echeq.fecha_pago) < moment()) {
                        disable = true;
                    }
                });
                break;
            }
            case "DEPOSIT": {
                selectedRows.forEach((echeq) => {
                    const fechaPago = moment(echeq.fecha_pago);
                    const fechaHoy = moment();
                    const diff = Math.abs(moment().diff(moment(echeq.fecha_pago), "days"));

                    if (echeq.estado !== "ACTIVO" || (fechaPago > fechaHoy && diff > 0) || diff > 30) {
                        disable = true;
                    }
                });
                break;
            }
            case "ENDORSE": {
                selectedRows.forEach((echeq) => {
                    if (echeq.estado !== "ACTIVO" || echeq.cheque_caracter.toLowerCase() === "no a la orden") {
                        disable = true;
                    }
                });
                break;
            }
            case "EMIT-CESION": {
                selectedRows.forEach((echeq) => {
                    if (echeq.estado !== "ACTIVO" || echeq.cheque_caracter.toLowerCase() !== "no a la orden") {
                        disable = true;
                    }
                });
                break;
            }
            case "NEGOTIATE": {
                selectedRows.forEach((echeq) => {
                    if (echeq.estado !== "ACTIVO") {
                        disable = true;
                    }
                });
                break;
            }
            case "ANULATE-ENDORSE": {
                selectedRows.forEach((echeq) => {
                    if (echeq.estado !== "ACTIVO-PENDIENTE") {
                        disable = true;
                    }
                });
                break;
            }
            case "ANULATE-CESION": {
                selectedRows.forEach((echeq) => {
                    if (echeq.estado !== "CESION-PENDIENTE") {
                        disable = true;
                    }
                });
                break;
            }
            default: {
                break;
            }
        }

        return disable;
    };

    const multipleOptions = multipleEcheckActions.map((value) => ({
        value,
        label: i18n.get(`${FORM_ID}.${value.replace("-", ".").toLowerCase()}.${FORM_ID}`).toUpperCase(),
        disabled: checkEnableAdmit(value),
    }));

    const receivedEcheqAction = ({ action }) => {
        const echeqList = selectedRows;
        const multipleEcheq = echeqList?.length > 1;

        dispatch(echeckActions.setEchecksToDoActions({ action, echeqList, echeqsListTotal }));

        switch (action) {
            case "ADMIT": {
                dispatch(push("/echecks/action/transaction/confirm"));
                break;
            }
            case "REPUDIATE": {
                setDrawer({
                    showDrawer: true,
                    contentDrawer: <RepudiateEcheckForm />,
                });
                break;
            }
            case "DEPOSIT": {
                setDrawer({
                    showDrawer: true,
                    contentDrawer: <DepositEcheckForm currency={echeqList?.[0]?.moneda} />,
                });
                break;
            }
            case "CUSTODY": {
                setDrawer({
                    showDrawer: true,
                    contentDrawer: <CustodyEcheq currency={echeqList?.[0]?.moneda} />,
                });
                break;
            }
            case "RESCUE": {
                dispatch(push("/echecks/action/confirmation"));
                break;
            }
            case "ENDORSE": {
                dispatch(push("/echecks/action/endorse"));
                break;
            }
            case "EMIT-CESION": {
                dispatch(push("/echecks/action/cesion"));
                break;
            }
            case "NEGOTIATE": {
                dispatch(push("/echeqs/action/negotiate"));
                break;
            }
            case "ANULATE-ENDORSE": {
                setDrawer({
                    showDrawer: true,
                    contentDrawer: <AnulateEcheckForm multipleEcheq={multipleEcheq} />,
                });
                break;
            }
            case "ANULATE-CESION": {
                setDrawer({
                    showDrawer: true,
                    contentDrawer: <AnulateCesionEcheq />,
                });
                break;
            }

            default:
                break;
        }
    };

    return (
        <>
            {selectedRows.length > 0 && !someIsExpired && (
                <Formik
                    initialValues={{ action: "" }}
                    onSubmit={receivedEcheqAction}
                    validationSchema={validationSchema}
                    ref={formRef}>
                    {({ values }) => (
                        <Form>
                            <Row className="multiple-action-selector box box-border-soft">
                                <Col xs={12} md={4} className={!isDesktop && "mb-8"}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="action"
                                        component={Selector}
                                        options={multipleOptions}
                                        hideLabel
                                    />
                                </Col>

                                <Col xs={12} md={2} mdOffset={10}>
                                    <Button
                                        type="submit"
                                        className="btn-outline"
                                        label="global.accept"
                                        disabled={!values.action}
                                    />
                                </Col>
                            </Row>
                        </Form>
                    )}
                </Formik>
            )}
        </>
    );
};

MultipleActionSelector.propTypes = {
    environmentCuit: string.isRequired,
    selectedRows: arrayOf(echeqPropTypes).isRequired,
    echeqsListTotal: number.isRequired,
    setDrawer: func.isRequired,
    isDesktop: bool.isRequired,
};

export default resizableRoute(MultipleActionSelector);
